import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import JsonLd from '@/components/base/JsonLd';
import { updateDocumentMeta } from '@/components/base/SeoHelmet';
import { aboutData } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    updateDocumentMeta(
      '关于我们 - 红桃视频在线观看',
      '了解红桃视频在线观看的品牌故事、发展历程、信誉保障及联系方式。红桃视频在线观看致力于为用户提供优质免费高清视频服务。',
      '红桃视频在线观看,关于红桃视频,红桃视频品牌,红桃视频联系方式,红桃视频信誉保障',
      'https://www.hongtaovideo.com/about'
    );
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qt2csnlsngrm4lm5hg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于红桃视频在线观看',
    description: aboutData.intro,
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '红桃视频在线观看',
    url: 'https://www.hongtaovideo.com/',
    description: '免费高清视频播放平台',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: aboutData.contact.phone,
      contactType: 'customer service',
      email: aboutData.contact.email,
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contact.address,
      addressCountry: 'CN',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hongtaovideo.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.hongtaovideo.com/about' },
    ],
  };

  return (
    <>
      <JsonLd data={aboutJsonLd} />
      <JsonLd data={orgJsonLd} />
      <JsonLd data={breadcrumbJsonLd} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="relative w-full min-h-[320px] md:min-h-[400px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20modern%20office%20interior%20with%20cinematic%20lighting%2C%20elegant%20workspace%20with%20wooden%20furniture%2C%20soft%20golden%20sunlight%20through%20large%20windows%2C%20professional%20atmosphere%2C%20creative%20studio%20design%2C%20rich%20warm%20tones&width=1600&height=600&seq=about-hero-01&orientation=landscape"
              alt="关于红桃视频在线观看"
              title="红桃视频在线观看 - 关于我们"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
            <div className="relative z-10 text-center px-4">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto">
                {aboutData.slogan}
              </p>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-6">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed text-center max-w-3xl mx-auto">
                {aboutData.intro}
              </p>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {aboutData.values.map((val) => (
                  <div key={val.title} className="p-5 rounded-lg bg-background-100 border border-background-200 text-center">
                    <div className="w-10 h-10 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-3">
                      <i className="ri-check-double-line text-accent-600"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm">
                      {val.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 leading-relaxed">
                      {val.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {aboutData.history.map((item, index) => (
                    <div key={item.year} className={`relative flex items-start gap-6 md:gap-10 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-background-50"></div>
                      </div>
                      <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-0' : 'md:text-left md:pl-0'} md:w-[calc(50%-2rem)]`}>
                        <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold mb-2">
                          {item.year}
                        </span>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.event}
                        </p>
                      </div>
                      {index % 2 === 0 ? <div className="hidden md:block flex-1"></div> : <div className="hidden md:block flex-1"></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                {aboutData.reputation.title}
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {aboutData.reputation.items.map((item) => (
                  <div key={item.label} className="p-6 rounded-lg bg-background-100 border border-background-200 text-center">
                    <div className="text-2xl md:text-3xl font-bold text-accent-600 font-heading">
                      {item.value}
                    </div>
                    <div className="mt-2 text-xs text-foreground-500">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">电子邮箱</div>
                      <a href={`mailto:${aboutData.contact.email}`} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
                        {aboutData.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">联系电话</div>
                      <a href={`tel:${aboutData.contact.phone}`} className="text-sm text-foreground-500 hover:text-primary-600 transition-colors">
                        {aboutData.contact.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">公司地址</div>
                      <p className="text-sm text-foreground-500">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">工作时间</div>
                      <p className="text-sm text-foreground-500">{aboutData.contact.workHours}</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                    在线留言
                  </h3>
                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-14 h-14 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-3">
                        <i className="ri-check-line text-2xl text-primary-600"></i>
                      </div>
                      <p className="text-sm text-foreground-700 font-medium">感谢您的留言！</p>
                      <p className="text-xs text-foreground-500 mt-1">我们会尽快回复您</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
                      <input type="text" name="phone_alt" className="site-honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      <div>
                        <label htmlFor="about-name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-xs font-medium text-foreground-600 mb-1.5">留言</label>
                        <textarea
                          id="about-message"
                          name="message"
                          rows={4}
                          required
                          maxLength={500}
                          className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                      </div>
                      {formStatus === 'error' && (
                        <p className="text-xs text-red-600">{formError}</p>
                      )}
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full px-4 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {formStatus === 'loading' ? '提交中...' : '提交留言'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}